import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { openWhatsApp } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";

export default function Navbar() {
  const { settings } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const brand = settings?.brand || {};
  const nav = settings?.nav || [];
  const waNumber = settings?.contact?.whatsapp || "";
  const waMsg = settings?.templates?.general || "Hi there!";

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-[#0B0B14]/85 border-b border-white/10" : "bg-transparent border-b border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="navbar-brand-link">
            <Logo settings={settings} size={38} className="float-y" />
            <div className="leading-tight hidden sm:block">
              <div className="font-display font-bold text-white text-base sm:text-lg tracking-tight">
                {brand.short_name || brand.name || "Scalovix"}
              </div>
              <div className="font-mono-label text-[9px] text-accent-bright hidden sm:block">
                {brand.tagline || ""}
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {nav.map((l, i) => (
              <NavLink
                key={i}
                to={l.href || "/"}
                data-testid={`nav-link-${(l.label || "link").toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors duration-300 hover:text-white ${isActive ? "text-white" : "text-mist/80"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Button
              asChild
              className="hidden sm:inline-flex rounded-full bg-accent hover:bg-accent-bright text-white font-semibold px-5 shadow-[0_0_24px_rgba(124,58,237,0.45)] transition-all duration-300 hover:shadow-[0_0_36px_rgba(168,85,247,0.6)] hover:-translate-y-0.5"
              data-testid="navbar-whatsapp-cta"
            >
              <a href={waNumber ? `https://wa.me/${String(waNumber).replace(/\D/g, "")}?text=${encodeURIComponent(waMsg)}` : "#"}
                target="_blank" rel="noopener noreferrer"
                onClick={() => openWhatsApp(waNumber, waMsg, { trackPath: "/navbar" }) || undefined}
              >
                WhatsApp Us
              </a>
            </Button>
            <button
              className="lg:hidden p-2 rounded-lg border border-white/10 text-white"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="navbar-mobile-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-x-0 top-16 lg:hidden z-[99] glass-strong border-b border-white/10"
            data-testid="mobile-drawer"
          >
            <nav className="px-6 py-6 flex flex-col gap-1">
              {nav.map((l, i) => (
                <NavLink
                  key={i}
                  to={l.href || "/"}
                  data-testid={`mobile-drawer-link-${(l.label || "link").toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-lg text-base font-medium ${isActive ? "bg-accent/15 text-white" : "text-mist hover:text-white"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Button asChild className="mt-4 rounded-full bg-accent hover:bg-accent-bright text-white font-semibold" data-testid="mobile-drawer-whatsapp-cta">
                <a
                  href={waNumber ? `https://wa.me/${String(waNumber).replace(/\D/g, "")}?text=${encodeURIComponent(waMsg)}` : "#"}
                  target="_blank" rel="noopener noreferrer"
                >
                  WhatsApp Us
                </a>
              </Button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
